<script lang="ts">
import { defineComponent, PropType } from "vue";
import { forceX } from "d3";

const periods = [
  {
    label: "Morning",
    hours: [
      "",
      ...Array(12)
        .fill(0)
        .map((_, i) => i.toString().padStart(2, "0") + "h"),
    ],
    offset: 0,
    indices: Array(48)
      .fill(0)
      .map((_, i) => i),
  },
  {
    label: "Afternoon",
    hours: [
      "",
      ...Array(12)
        .fill(0)
        .map((_, i) => (i + 12).toString().padStart(2, "0") + "h"),
    ],
    indices: Array(48)
      .fill(0)
      .map((_, i) => i + 48),
  },
];

export default defineComponent({
  emits: ["change"],
  props: {
    numValues: {
      type: Number,
      default: 1,
    },
    slots: {
      type: Array as PropType<number[]>,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    exclude: {
      type: Boolean,
      default: false,
    },
    forceLabels: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { values: [...this.slots], periods, span: 1 };
  },
  methods: {
    forceX,
    toggle(idx: number) {
      const value = (this.values[idx] + 1) % (this.numValues + 1);
      const indices = Array(this.span)
        .fill(0)
        .map((_, i) => idx + i);
      indices.forEach((idx) => (this.values[idx] = value));
      this.$emit("change", this.values);
    },
  },
  watch: {
    slots: {
      handler(slots) {
        this.values = [...slots];
      },
    },
  },
  computed: {
    shownSlots() {
      return this.values.reduce((items, slot, slotIdx) => {
        if (slotIdx % this.span === 0) {
          const indices = Array(this.span)
            .fill(0)
            .map((_, i) => slotIdx + i);
          const value = this.values[slotIdx];
          const mixed = !indices.every((i) => this.values[i] === value);
          return [...items, { mixed, value }];
        }
        return items;
      }, [] as { value: number; mixed: boolean }[]);
    },
  },
});
</script>

<template>
  <div class="flex items-center mt-4">
    <div class="font-medium grow text-sm text-ellipsis shrink-0">
      {{ $t(exclude ? "TimeSlotsProhibited" : "TimeSlotsTitle") }}
    </div>
    <div
      class="pill flex-shrink-0"
      @click="span = 1"
      :class="{ active: span === 1 }"
    >
      15 min
    </div>
    <div
      class="pill flex-shrink-0"
      @click="span = 2"
      :class="{ active: span === 2 }"
    >
      30 min
    </div>
    <div
      class="pill flex-shrink-0"
      @click="span = 4"
      :class="{ active: span === 4 }"
    >
      60 min
    </div>
  </div>
  <div class="my-5 flex justify-evenly">
    <div
      v-for="period in periods"
      :key="period.label"
      class="grid grid-cols-5 gap-1 first:mr-5"
    >
      <div></div>
      <div class="col-span-4 text-center text-xs mb-2">
        {{ $t(period.label) }}
      </div>

      <div
        class="text-[10px] self-center"
        v-for="(hour, idx) in period.hours"
        :key="hour"
        :style="{ 'grid-row': idx + 2 }"
      >
        {{ hour }}
      </div>
      <template v-for="(time, idx) in ['00', '15', '30', '45']" :key="time">
        <div
          class="text-white text-[10px] text-center"
          v-if="idx % span === 0"
          :class="`col-span-${span}`"
        >
          {{ time }}
        </div>
      </template>
      <template v-for="idx in period.indices" :key="idx"
        ><div
          @click="toggle(idx)"
          class="slot overflow-hidden relative"
          :class="
            `col-span-${span} ` +
            (readOnly ? 'pointer-events-none' : 'cursor-pointer')
          "
          v-if="idx % span === 0"
        >
          <div
            class="w-full absolute bottom-0 flex items-center justify-center h-full text-xs"
            :class="{
              hidden:
                !shownSlots[idx / span].mixed &&
                shownSlots[idx / span].value === 0,
              'bg-midblue': !shownSlots[idx / span].mixed && !exclude,
              'bg-red-300': !shownSlots[idx / span].mixed && exclude,
              'min-h-full bg-striped-midblue':
                shownSlots[idx / span].mixed && !exclude,
              'min-h-full bg-striped-red':
                shownSlots[idx / span].mixed && exclude,
            }"
          >
            <div
              v-show="
                forceLabels ||
                (numValues > 1 && shownSlots[idx / span].value > 0)
              "
            >
              {{
                shownSlots[idx / span].mixed
                  ? "-"
                  : shownSlots[idx / span].value
              }}
            </div>
          </div>
        </div></template
      >
    </div>
  </div>
</template>

<style scoped>
.slot {
  height: 24px;
  min-width: 24px;
  border-radius: 5px;
  background: linear-gradient(
    216deg,
    rgba(255, 255, 255, 0.35) -31.97%,
    rgba(255, 255, 255, 0.1) 114.27%
  );
}

.slot.col-span-1 {
  width: 24px;
}

.slot.col-span-2 {
  width: 52px;
}

.slot.col-span-4 {
  width: 108px;
}

.bg-striped-midblue {
  background: repeating-linear-gradient(
    -45deg,
    rgb(var(--color-primary)),
    rgb(var(--color-primary)) 10px,
    #6b6967 10px,
    #6b6967 20px
  );
}
</style>
